/**
 * The sentences the festival page's header and dialogs say. The preview lines, the suggestion texts and `status_label`
 * are Tina's own and shown as they come; what is here is IMS's: the sub-line, and what a confirm dialog tells the
 * curator before something they cannot take back.
 */
import { dayRange } from "@/lib/tina/festivals";
import { foldName } from "@/pages/tina/festivals/lib/page-form";
import type {
  FestivalListRow,
  FestivalPageRead,
} from "@/types/tina-festival-page";

const plural = (count: number, noun: string): string =>
  count === 1 ? `${count} ${noun}` : `${count} ${noun}s`;

/** The line under the festival's name: city, dates, and what Tina found. */
export function headerSubLine(page: FestivalPageRead): string {
  const when = dayRange(page.first_day, page.last_day);
  if (page.kind === "program") {
    const venues = new Set(
      page.concerts.map((c) => c.venue_id ?? c.venue_name ?? c.mood_event_id),
    );
    const live =
      page.concerts.length > 0 &&
      page.concerts.every(
        (c) => c.visibility === "live" || c.visibility === null,
      );
    return [
      page.city,
      when,
      `${plural(page.concerts.length, "concert")} at ${plural(venues.size, "venue")}`,
      live ? "every concert is already live in Mood" : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return [page.city, when, `found on ${plural(page.site_count, "site")}`]
    .filter(Boolean)
    .join(" · ");
}

const DIRTY_SENTENCE = "Your unsaved edits on this page are dropped.";

/** What Reject says it does. */
export function rejectText(page: FestivalPageRead): {
  title: string;
  description: string;
} {
  return {
    title: `Reject ${page.name}?`,
    description:
      page.kind === "day_ticket"
        ? "Tina treats it as not a festival: the pages it held publish as normal events, and later pages are not held."
        : "No group is made. Its concerts stay normal events.",
  };
}

/** What changing the festival's type does, by where the festival stands (Plan 8's table). */
export function kindChangeText(
  page: FestivalPageRead,
  to: "day_ticket" | "program",
  dirty: boolean,
): { title: string; description: string } {
  const approved = page.state !== "proposed";
  let description: string;
  if (to === "program") {
    description = approved
      ? "The day events stay in Mood as normal events in the group. Concerts Tina finds later join the group."
      : "Each concert publishes as a normal event and the festival waits for Approve group. The pages Tina held publish.";
  } else {
    description = approved
      ? "The page asks for the days, venues and lineups again. The concerts show under Already in Mood, and the group stays until you approve."
      : "Tina makes one event per day when you approve. Its concerts already in Mood show under Already in Mood.";
  }
  return {
    title: `Make ${page.name} a ${to === "program" ? "program" : "day-ticket"} festival?`,
    description: dirty ? `${description} ${DIRTY_SENTENCE}` : description,
  };
}

/** The festivals this one can be merged into: the same city (folded), not itself, by name. Tina judges the merge. */
export function mergeCandidates(
  rows: FestivalListRow[],
  page: FestivalPageRead,
): FestivalListRow[] {
  if (page.city === null) return [];
  const city = foldName(page.city);
  return rows
    .filter(
      (row) =>
        row.record_id !== page.record_id &&
        row.city !== null &&
        foldName(row.city) === city,
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** What Unpublish says goes and what stays, from what the page read (Plan 8's rule for an unpublish). */
export function unpublishText(page: FestivalPageRead): {
  title: string;
  deleted: string[];
  staying: string[];
  description: string;
} {
  const title = `Unpublish ${page.name}?`;
  if (page.kind === "program") {
    return {
      title,
      deleted: [],
      staying: [],
      description:
        "Tina deletes the festival's group. Its concerts stay in Mood as normal events. The festival goes back to Group waiting, and Approve group makes the group again.",
    };
  }
  const used = page.existing.filter((line) => line.choice === "use_as_day");
  const usedIds = new Set(used.map((line) => line.mood_event_id));
  const made = page.days.filter(
    (day) => day.mood_event_id !== null && !usedIds.has(day.mood_event_id),
  );
  const nameOf = (name: string | null, id: string | null): string =>
    name ?? id ?? "";
  const saved = made.filter((day) => (day.saves ?? 0) > 0);
  return {
    title,
    deleted: made
      .filter((day) => (day.saves ?? 0) === 0)
      .map((day) => nameOf(day.name, day.mood_event_id)),
    staying: [
      ...saved.map(
        (day) =>
          `${nameOf(day.name, day.mood_event_id)} (${day.saves} ${day.saves === 1 ? "save" : "saves"})`,
      ),
      ...used.map((line) => line.name),
    ],
    description:
      "Tina also deletes the festival's group. The festival goes back to Waiting for approval with everything on this page kept, and Approve and publish puts it back in Mood.",
  };
}

/** The sentence under a dialog's text when the page holds edits the action throws away. */
export const DIRTY_DROPPED = DIRTY_SENTENCE;
